import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Card,
  CardContent,
  Grid,
  Box,
  Chip,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Button,
  DialogActions,
} from "@mui/material";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { FilterList } from "@mui/icons-material";
import CrudService from "services/cruds-service";

const BatchLogs = ({ currentBatchData }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [batchData, setBatchData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const [jobTypesDialogOpen, setJobTypesDialogOpen] = useState({
    open: false,
    data: [],
  });

  useEffect(() => {
    const fetchBatchData = async () => {
      try {
        setLoader(true);
        const response = await CrudService.getBatches();
        setBatchData(response.data);
      } catch (err) {
        console.error("Error fetching batch data:", err);
      } finally {
        setLoader(false);
      }
    };

    fetchBatchData();
  }, []);

  useEffect(() => {
    if (currentBatchData) {
      const { batchId, status } = currentBatchData;
      setBatchData((prevBatchData) =>
        prevBatchData.map((data) =>
          data.key.split(":")[1] === batchId
            ? {
                ...data,
                value: {
                  ...data.value,
                  status: status,
                },
              }
            : data
        )
      );
    }
  }, [currentBatchData]);

  const handleStatusFilter = useCallback((status) => {
    setStatusFilter(status);
    setIsFilterOpen(false);
  }, []);

  const filteredBatchData = useMemo(() => {
    return batchData.filter((data) => {
      const matchesSearch = data.key
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "ALL" || data.value.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [batchData, searchTerm, statusFilter]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return null;
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString("en-US", options);
  }, []);

  const stopJob = useCallback(async (batchId) => {
    try {
      const response = await CrudService.stopJob(batchId);
      if (response.success) {
        setBatchData((prevBatchData) =>
          prevBatchData.map((data) =>
            data.key.split(":")[1] === batchId
              ? {
                  ...data,
                  value: {
                    ...data.value,
                    status: "STOPPED",
                  },
                }
              : data
          )
        );
      }
    } catch (err) {
      console.error("Error stopping job:", err);
    }
  }, []);

  const StatBox = ({ label, value }) => (
    <Box
      sx={{
        background: darkMode ? "#202940" : "#FFF",
        p: 1,
        borderRadius: 2,
        textAlign: "center",
        transition: "transform 0.3s",
        "&:hover": {
          transform: "scale(1.05)",
        },
      }}
    >
      <MDTypography variant="h6" gutterBottom>
        {label}
      </MDTypography>
      <MDTypography variant="h5">{value}</MDTypography>
    </Box>
  );

  const DateDisplay = ({ label, date }) => {
    if (!date) return null;
    return (
      <MDTypography variant="caption" fontWeight="medium">
        {label} : {date}
      </MDTypography>
    );
  };

  const handleShowJobTypes = useCallback((jobsDetails) => {
    setJobTypesDialogOpen({
      open: true,
      data: jobsDetails || [],
    });
  }, []);

  return (
    <Box
      sx={{
        border: "1px solid #ccc",
        borderRadius: "8px",
        height: { xs: "50vh", md: "calc(100vh - 220px)" },
        background: darkMode ? "#202940" : "#FFF",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ p: 2, borderBottom: "1px solid #ccc" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <MDTypography variant="h2" sx={{ flexShrink: 0 }}>
            Batches
          </MDTypography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              width: "100%",
              maxWidth: "500px",
            }}
          >
            <MDInput
              placeholder="Search..."
              value={searchTerm}
              fullWidth
              onChange={({ currentTarget }) =>
                setSearchTerm(currentTarget.value)
              }
            />
            <MDButton
              variant="outlined"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              startIcon={<FilterList />}
            >
              Filter
            </MDButton>
          </Box>
        </Box>
        {isFilterOpen && (
          <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {["ALL", "IN_PROGRESS", "COMPLETED", "SCHEDULED", "STOPPED"].map(
              (status) => (
                <Chip
                  key={status}
                  label={status}
                  onClick={() => handleStatusFilter(status)}
                  color={statusFilter === status ? "primary" : "default"}
                  sx={{ cursor: "pointer" }}
                />
              )
            )}
          </Box>
        )}
      </Box>
      <Box sx={{ overflowY: "auto", flexGrow: 1, p: 2 }}>
        {loader
          ? [...Array(3)].map((_, index) => <StackSkeleton key={index} />)
          : filteredBatchData.map((data) => {
              const { key, value } = data;
              const {
                total,
                done,
                failed,
                success,
                progressPercentage,
                status,
                scheduleAt,
                createdAt,
                body,
                options,
              } = value;
              const jobsDetails = body?.jobsDetails;

              return (
                <Card
                  key={key}
                  raised
                  sx={{
                    mb: 3,
                    overflow: "visible",
                    bgcolor: darkMode
                      ? "background.default"
                      : "background.paper",
                  }}
                >
                  <CardContent>
                    <Grid container spacing={3} alignItems="center">
                      <Grid
                        item
                        xs={12}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <MDTypography variant="h5" fontWeight="medium">
                          Batch ID: {key.split(":")[1]}
                        </MDTypography>
                        <Box display="flex" alignItems="center" gap={2}>
                          <MDButton
                            variant="contained"
                            color="info"
                            size="small"
                            onClick={() => handleShowJobTypes(jobsDetails)}
                          >
                            Show Job Types
                          </MDButton>
                          <Chip label={status} sx={{ fontWeight: "bold" }} />
                          {status === "IN_PROGRESS" && (
                            <MDButton
                              variant="contained"
                              color="secondary"
                              size="small"
                              onClick={() => stopJob(key.split(":")[1])}
                            >
                              Stop
                            </MDButton>
                          )}
                          {status === "SCHEDULED" && (
                            <MDButton
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={() => stopJob(key.split(":")[1])}
                            >
                              Cancel
                            </MDButton>
                          )}
                        </Box>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={4}
                        display="flex"
                        justifyContent="center"
                      >
                        <Box position="relative" display="inline-flex">
                          <CircularProgress
                            variant="determinate"
                            value={progressPercentage}
                            size={120}
                            thickness={5}
                            sx={{ color: "info.main" }}
                          />
                          <Box
                            top={0}
                            left={0}
                            bottom={0}
                            right={0}
                            position="absolute"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <MDTypography variant="h5" component="div">
                              {`${Math.round(progressPercentage)}%`}
                            </MDTypography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={3}>
                            <StatBox label="Total" value={total} />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <StatBox label="Done" value={done} />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <StatBox label="Success" value={success} />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <StatBox label="Failed" value={failed} />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <StatBox
                              label="Concurrency"
                              value={options?.concurrency || "N/A"}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <StatBox
                              label="Limiter Max"
                              value={options?.limiter?.max || "N/A"}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <StatBox
                              label="Limiter Duration"
                              value={
                                options?.limiter?.duration
                                  ? `${
                                      options?.limiter?.duration / 1000
                                    } second`
                                  : "N/A"
                              }
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      {(createdAt || scheduleAt) && (
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 2,
                            }}
                          >
                            <DateDisplay
                              label="Created At"
                              date={formatDate(createdAt)}
                            />
                            <DateDisplay
                              label="Scheduled At"
                              date={formatDate(scheduleAt)}
                            />
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              );
            })}
      </Box>

      <Dialog
        open={jobTypesDialogOpen.open}
        onClose={() => setJobTypesDialogOpen({ open: false, data: [] })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <MDTypography variant="h5" fontWeight="medium">
            Job Types
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          {jobTypesDialogOpen.data.length === 0 ? (
            <MDTypography
              variant="body1"
              color="textSecondary"
              sx={{ textAlign: "center", py: 2 }}
            >
              No Job Types available
            </MDTypography>
          ) : (
            <List>
              {jobTypesDialogOpen.data.map((jobType) => (
                <ListItem
                  key={jobType.job_type_id}
                  sx={{
                    padding: "4px 16px",
                    borderBottom: "1px solid #eee",
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                  <ListItemText
                    primary={
                      <MDTypography variant="body1" fontWeight="small">
                        {jobType.name}
                      </MDTypography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setJobTypesDialogOpen({ open: false, data: [] })}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function StackSkeleton() {
  return (
    <Card raised sx={{ mb: 3, overflow: "hidden" }}>
      <CardContent>
        <Grid container spacing={3} alignItems="center">
          {/* Header Section */}
          <Grid item xs={12} display="flex" justifyContent="space-between">
            <Skeleton variant="text" width="40%" height={30} />
            <Skeleton variant="rounded" width={80} height={30} />
          </Grid>

          {/* Circular Progress Placeholder */}
          <Grid item xs={12} sm={4} display="flex" justifyContent="center">
            <Skeleton variant="circular" width={100} height={100} />
          </Grid>

          {/* Stats Section */}
          <Grid item xs={12} sm={8}>
            <Grid container spacing={2}>
              {[...Array(4)].map((_, index) => (
                <Grid key={index} item xs={6} sm={3}>
                  <Skeleton variant="rectangular" width="100%" height={50} />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Date Info */}
          <Grid item xs={12} display="flex" justifyContent="space-between">
            <Skeleton variant="text" width="40%" height={20} />
            <Skeleton variant="text" width="40%" height={20} />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

export default BatchLogs;
